/**
 * AUIGrid 엑셀 스타일 Fill Handle (채우기 핸들)
 *
 * 동작:
 *   - 셀 선택 시 우하단에 파란 점(핸들) 표시
 *   - 핸들 더블클릭: 선택 셀 값으로 아래 빈 셀 자동 채움
 *   - 핸들 드래그: 드래그 범위만큼 값 채움
 *   - Undo 지원: Ctrl+Z로 채우기 되돌리기
 */

const HANDLE_SIZE = 7; // 핸들 크기(px)

export function setupFillHandle(gridInstance, containerSelector, options = {}) {
  const { onFill } = options; // onFill(undoEntries) → 외부 undo 스택에 push
  // 약간의 지연 후 초기화 (그리드 DOM 준비 대기)
  const initTimer = setTimeout(() => {
    try {
      const container = document.querySelector(containerSelector);
      if (!container) {
        console.warn('[FillHandle] container not found:', containerSelector);
        return;
      }

      // 이전 핸들러 제거
      if (container._fillHandle) {
        cleanup(container);
      }

      const state = {
        handle: null,         // 핸들 DOM 요소
        overlay: null,        // 드래그 중 하이라이트 오버레이
        isDragging: false,
        startRow: -1,
        startCol: -1,
        startField: '',
        startValue: null,
        currentRow: -1,
      };

      // ─── 핸들 DOM 생성 ───
      const handle = document.createElement('div');
      handle.className = 'aui-fill-handle';
      handle.style.cssText = `
        position: absolute;
        width: ${HANDLE_SIZE}px;
        height: ${HANDLE_SIZE}px;
        background: #2563EB;
        border: 1px solid #1D4ED8;
        cursor: crosshair;
        z-index: 1000;
        display: none;
        pointer-events: auto;
        box-sizing: border-box;
      `;
      container.style.position = container.style.position || 'relative';
      container.appendChild(handle);
      state.handle = handle;

      // ─── 드래그 하이라이트 오버레이 ───
      const overlay = document.createElement('div');
      overlay.className = 'aui-fill-overlay';
      overlay.style.cssText = `
        position: absolute;
        background: rgba(37, 99, 235, 0.08);
        border: 1px dashed #2563EB;
        z-index: 999;
        display: none;
        pointer-events: none;
        box-sizing: border-box;
      `;
      container.appendChild(overlay);
      state.overlay = overlay;

      // ─── 핸들 위치 업데이트 ───
      const updateHandlePosition = () => {
        if (!window.AUIGrid?.isCreated(gridInstance)) {
          handle.style.display = 'none';
          return;
        }

        try {
          const selectedItems = window.AUIGrid.getSelectedItems(gridInstance);
          if (!selectedItems || selectedItems.length === 0) {
            handle.style.display = 'none';
            return;
          }

          // 마지막 선택 아이템 (블록 선택 시 우하단)
          const lastSel = selectedItems[selectedItems.length - 1];
          const rowIdx = lastSel.rowIndex;
          const colIdx = lastSel.columnIndex;
          const dataField = lastSel.dataField;

          if (rowIdx < 0 || colIdx < 0 || !dataField) {
            handle.style.display = 'none';
            return;
          }

          // pageNumber, idx 등 editable: false 컬럼에서는 핸들 숨김
          const colInfo = window.AUIGrid.getColumnInfoList(gridInstance);
          const colDef = colInfo?.find(c => c.dataField === dataField);
          if (colDef && colDef.editable === false) {
            handle.style.display = 'none';
            return;
          }

          // 셀 DOM 요소 찾기
          const cellEl = window.AUIGrid.getCellElementByIndex(gridInstance, rowIdx, colIdx);
          if (!cellEl) {
            handle.style.display = 'none';
            return;
          }

          const cellRect = cellEl.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();

          // 셀이 컨테이너 밖에 있으면 숨김
          if (cellRect.bottom < containerRect.top || cellRect.top > containerRect.bottom) {
            handle.style.display = 'none';
            return;
          }

          // 핸들 위치: 셀의 우하단 모서리
          const left = cellRect.right - containerRect.left - HANDLE_SIZE / 2 - 1;
          const top = cellRect.bottom - containerRect.top - HANDLE_SIZE / 2 - 1;

          handle.style.left = left + 'px';
          handle.style.top = top + 'px';
          handle.style.display = 'block';

          // 시작 위치 저장
          state.startRow = rowIdx;
          state.startCol = colIdx;
          state.startField = dataField;

          // 첫 번째 선택 셀의 값 (채울 값)
          const firstSel = selectedItems[0];
          const item = firstSel.item || firstSel;
          state.startValue = item[firstSel.dataField];

        } catch (e) {
          handle.style.display = 'none';
        }
      };

      // ─── 더블클릭: 아래 빈 셀 자동 채움 ───
      const handleDblClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!window.AUIGrid?.isCreated(gridInstance)) return;

        const { startRow, startField, startValue } = state;
        if (startRow < 0 || !startField || startValue === undefined || startValue === null || startValue === '') return;

        const gridData = window.AUIGrid.getGridData(gridInstance);
        if (!gridData || gridData.length === 0) return;

        const undoEntries = [];
        let filled = 0;

        // 아래쪽으로 빈 셀 채우기 (비어있지 않은 셀을 만나면 중지)
        for (let r = startRow + 1; r < gridData.length; r++) {
          const cellVal = gridData[r][startField];
          if (cellVal !== undefined && cellVal !== null && cellVal !== '') {
            break; // 값이 있는 셀을 만나면 중지
          }
          undoEntries.push({ rowIndex: r, dataField: startField, oldValue: cellVal });
          window.AUIGrid.setCellValue(gridInstance, r, startField, startValue);
          filled++;
        }

        if (filled > 0) {
          console.log(`✅ [FillHandle] 더블클릭 채우기: "${startValue}" × ${filled}행 (${startField})`);
          // 외부 undo 스택에 push
          if (onFill) onFill(undoEntries);
          try { window.AUIGrid.refresh(gridInstance); } catch (e) { }
        }
      };

      // ─── 드래그 시작 ───
      const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!window.AUIGrid?.isCreated(gridInstance)) return;

        state.isDragging = true;
        state.currentRow = state.startRow;

        document.addEventListener('mousemove', handleMouseMove, true);
        document.addEventListener('mouseup', handleMouseUp, true);
      };

      // ─── 드래그 중: 오버레이 업데이트 ───
      const handleMouseMove = (e) => {
        if (!state.isDragging) return;

        const containerRect = container.getBoundingClientRect();
        const relY = e.clientY - containerRect.top;

        // 현재 마우스 위치의 행 인덱스 계산
        const gridData = window.AUIGrid.getGridData(gridInstance);
        if (!gridData) return;

        // 행 높이 기반으로 대상 행 추정
        let targetRow = -1;
        for (let r = 0; r < gridData.length; r++) {
          const cellEl = window.AUIGrid.getCellElementByIndex(gridInstance, r, state.startCol);
          if (cellEl) {
            const cellRect = cellEl.getBoundingClientRect();
            if (e.clientY >= cellRect.top && e.clientY <= cellRect.bottom) {
              targetRow = r;
              break;
            }
          }
        }

        if (targetRow < 0 || targetRow <= state.startRow) {
          overlay.style.display = 'none';
          state.currentRow = state.startRow;
          return;
        }

        state.currentRow = targetRow;

        // 오버레이 표시: startRow+1 ~ targetRow 범위
        try {
          const startCellEl = window.AUIGrid.getCellElementByIndex(
            gridInstance, state.startRow + 1, state.startCol
          );
          const endCellEl = window.AUIGrid.getCellElementByIndex(
            gridInstance, targetRow, state.startCol
          );

          if (startCellEl && endCellEl) {
            const startRect = startCellEl.getBoundingClientRect();
            const endRect = endCellEl.getBoundingClientRect();

            overlay.style.left = (startRect.left - containerRect.left) + 'px';
            overlay.style.top = (startRect.top - containerRect.top) + 'px';
            overlay.style.width = startRect.width + 'px';
            overlay.style.height = (endRect.bottom - startRect.top) + 'px';
            overlay.style.display = 'block';
          }
        } catch (e) {
          overlay.style.display = 'none';
        }
      };

      // ─── 드래그 종료: 값 채우기 ───
      const handleMouseUp = (e) => {
        document.removeEventListener('mousemove', handleMouseMove, true);
        document.removeEventListener('mouseup', handleMouseUp, true);

        overlay.style.display = 'none';

        if (!state.isDragging) return;
        state.isDragging = false;

        const { startRow, startField, startValue, currentRow } = state;

        if (currentRow <= startRow || !startField) return;
        if (startValue === undefined || startValue === null) return;

        const gridData = window.AUIGrid.getGridData(gridInstance);
        if (!gridData) return;

        const undoEntries = [];
        let filled = 0;

        for (let r = startRow + 1; r <= Math.min(currentRow, gridData.length - 1); r++) {
          const oldVal = gridData[r][startField];
          undoEntries.push({ rowIndex: r, dataField: startField, oldValue: oldVal });
          window.AUIGrid.setCellValue(gridInstance, r, startField, startValue);
          filled++;
        }

        if (filled > 0) {
          console.log(`✅ [FillHandle] 드래그 채우기: "${startValue}" × ${filled}행 (${startField})`);
          // 외부 undo 스택에 push
          if (onFill) onFill(undoEntries);
          try { window.AUIGrid.refresh(gridInstance); } catch (e) { }
        }
      };

      // ─── 이벤트 바인딩 ───
      handle.addEventListener('dblclick', handleDblClick, true);
      handle.addEventListener('mousedown', handleMouseDown, true);

      // AUIGrid 이벤트: 선택 변경 시 핸들 위치 업데이트
      window.AUIGrid.bind(gridInstance, 'selectionChange', () => {
        // 약간 지연: AUIGrid 내부 처리 완료 후 위치 계산
        requestAnimationFrame(updateHandlePosition);
      });

      window.AUIGrid.bind(gridInstance, 'cellClick', () => {
        requestAnimationFrame(updateHandlePosition);
      });

      // 스크롤 시 핸들 위치 업데이트
      window.AUIGrid.bind(gridInstance, 'vScrollChange', () => {
        requestAnimationFrame(updateHandlePosition);
      });
      window.AUIGrid.bind(gridInstance, 'hScrollChange', () => {
        requestAnimationFrame(updateHandlePosition);
      });

      // 편집 종료 시 핸들 재위치
      window.AUIGrid.bind(gridInstance, 'cellEditEnd', () => {
        setTimeout(updateHandlePosition, 50);
      });

      // 정리용 참조 저장
      container._fillHandle = {
        handle,
        overlay,
        state,
      };

      console.log('[FillHandle] setup OK:', containerSelector);

    } catch (e) {
      console.warn('[FillHandle] setup error:', e.message);
    }
  }, 1000); // 그리드 완전 초기화 후 실행

  return () => {
    clearTimeout(initTimer);
  };
}

/** 정리 함수 */
function cleanup(container) {
  if (!container._fillHandle) return;
  const { handle, overlay } = container._fillHandle;
  try {
    if (handle) handle.remove();
    if (overlay) overlay.remove();
  } catch (e) { }
  container._fillHandle = null;
}

export function destroyFillHandle(containerSelector) {
  try {
    const container = document.querySelector(containerSelector);
    if (container) cleanup(container);
  } catch (e) { }
}
